import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_INVITE_COPY } from '@/copy/modals'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type Invitation = components['schemas']['Invitation']
type OrgRole = Exclude<components['schemas']['OrgRole'], 'OWNER'>
type WorkspaceRole = components['schemas']['WorkspaceRole']

const ORG_ROLES: OrgRole[] = ['ADMIN', 'OPERATOR', 'AUDITOR']

/** Prototype_v2.html draws one checkbox per workspace, with no per-grant role picker — the
 * simplification this component follows rather than adding a control the design doesn't draw.
 * The granted `WorkspaceRole` is derived from the invited `OrgRole`, which is well-defined because
 * a workspace grant only ever narrows reach, never raises the ceiling (§3.1b): Operator grants
 * Operator, Auditor grants Viewer. Reported here as a UI simplification, not a spec requirement. */
function impliedWorkspaceRole(orgRole: OrgRole): WorkspaceRole {
  return orgRole === 'AUDITOR' ? 'VIEWER' : 'OPERATOR'
}

/**
 * `m-invite` — process/09-SCREEN-SPECS/m-invite.md. Owner is not invitable (`InvitationCreate`'s
 * own note) — `ORG_ROLES` omits it structurally rather than filtering it at render time.
 *
 * **`note` added W159** — `grantsNote` was already rendered, as a raw `<p>` instead of the shared
 * `Note` component.
 */
export function MInvite({
  orgId,
  workspaces,
  onDismiss,
  onInvited,
}: {
  orgId: string
  workspaces: readonly WorkspaceSummary[]
  onDismiss: () => void
  onInvited: (invitation: Invitation) => void
}) {
  const [email, setEmail] = useState('')
  const [orgRole, setOrgRole] = useState<OrgRole>('OPERATOR')
  const [grantedWorkspaceIds, setGrantedWorkspaceIds] = useState<ReadonlySet<string>>(new Set())

  const invite = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const workspace_grants =
        orgRole === 'ADMIN'
          ? undefined
          : [...grantedWorkspaceIds].map((workspace_id) => ({ workspace_id, workspace_role: impliedWorkspaceRole(orgRole) }))
      const response = await client.POST('/orgs/{org_id}/invitations', {
        params: { path: { org_id: orgId } },
        body: { email, org_role: orgRole, workspace_grants },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      return unwrap(response as { data?: Invitation; error?: Problem })
    },
    onSuccess: onInvited,
  })

  const problem = invite.error instanceof ApiProblemError ? invite.error.problem : undefined
  const headingId = 'm-invite-heading'

  return (
    <Modal dataScr={unitId('m-invite')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={460}>
      <ModalHeader id={headingId} heading={M_INVITE_COPY.heading}>
        {M_INVITE_COPY.subheading}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}
        <div className="flex flex-col gap-7">
          <Field label={M_INVITE_COPY.emailLabel}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg"
            />
          </Field>

          <div data-cmp="field">
            <span className="block mb-3 text-small font-semibold text-tx2">{M_INVITE_COPY.roleLabel}</span>
            <div data-cmp="radio-group" className="flex flex-col gap-4 items-start">
              {ORG_ROLES.map((role) => (
                <label key={role} className="text-body-lg">
                  <input type="radio" name="invr" checked={orgRole === role} onChange={() => setOrgRole(role)} />{' '}
                  {role.charAt(0) + role.slice(1).toLowerCase()} <span className="text-small text-muted">{M_INVITE_COPY.roleDescriptions[role]}</span>
                </label>
              ))}
            </div>
          </div>

          {orgRole !== 'ADMIN' && (
            <div data-cmp="field">
              <span className="block mb-3 text-small font-semibold text-tx2">{M_INVITE_COPY.grantsLabel}</span>
              <div data-cmp="radio-group" className="flex flex-col gap-3 items-start">
                {workspaces.map((ws) => (
                  <label key={ws.workspace_id} className="text-body-lg">
                    <input
                      type="checkbox"
                      checked={grantedWorkspaceIds.has(ws.workspace_id)}
                      onChange={(e) => {
                        const next = new Set(grantedWorkspaceIds)
                        if (e.target.checked) next.add(ws.workspace_id)
                        else next.delete(ws.workspace_id)
                        setGrantedWorkspaceIds(next)
                      }}
                    />{' '}
                    {ws.name}
                  </label>
                ))}
              </div>
              <Note>{M_INVITE_COPY.grantsNote}</Note>
            </div>
          )}

          <Strip>{M_INVITE_COPY.footerNote}</Strip>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_INVITE_COPY.cancelButton}
        </Button>
        <Button data-act={actionId('invitation.create')} disabled={!email || invite.isPending} onClick={() => invite.mutate()}>
          {M_INVITE_COPY.sendButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
